import { Seo } from "../components/Seo";
import { MaskedLine, Reveal } from "../components/Reveal";
import { CTABanner } from "../components/sections/CTABanner";
import { VendorMarquee } from "../components/sections/VendorStack";
import { IMAGES } from "../data/services";

const PROMISES = [
  {
    title: "Unlimited support, no meter running",
    body: "Sempresera never places a cap on how many support hours a client requests. Some MSPs charge extra when you need them most — we don't. Message our support as often as you need, at no additional cost. We're available whenever you need help, not when it's convenient for us.",
  },
  {
    title: "A standing CEO day, every month",
    body: "Every customer gets at least one day each month when our CEO personally handles their support issues. It's how our CEO speaks with every client, stays close to the work, and confirms — firsthand — that you're happy with our service.",
  },
  {
    title: "Every IT problem, covered",
    body: "Email, security, cloud, networks, printers, phones, vendors, backups — we cover all manner of IT problems for our customers. One accountable team for everything with a plug, a login or an IP address.",
  },
  {
    title: "Switching without the fear",
    body: "All too often, businesses stay with IT providers who aren't taking care of them because change feels risky. We make leaving your current provider painless: we document your environment, handle the handover, and keep you running through every step.",
  },
];

const VALUES = [
  { title: "Level the playing field", body: "Large corporations take world-class IT for granted. We exist so a five-person firm can expect exactly the same — the same vigilance, the same tooling, the same calm." },
  { title: "One standard, no tiers", body: "We refuse to sell a bare minimum and upsell the rest. Every client gets the full stack and the full team. What others charge extra for is simply our standard." },
  { title: "Plain talk", body: "No jargon wall, no fine print. If something isn't worth your money, we'll tell you. If we make a mistake, you'll hear it from us first." },
  { title: "Small by choice", body: "We don't pursue organizations over 25 employees. Staying small-business-only keeps us sharp at what actually matters to owners like you." },
];

export default function About() {
  return (
    <>
      <Seo
        title="About Sempresera | National Service Provider for Small Business IT"
        description="Founded in 2015, Sempresera levels the playing field between small businesses and large enterprises: unlimited support hours, a monthly CEO day for every client, and one all-inclusive IT standard."
        path="/about"
      />

      <section className="relative overflow-hidden" data-testid="about-hero">
        <div className="mx-auto max-w-7xl px-5 pb-20 pt-36 md:px-8 md:pt-44">
          <MaskedLine delay={0.1}>
            <span className="eyebrow">About Sempresera · Est. 2015</span>
          </MaskedLine>
          <h1 className="mt-6 max-w-4xl font-display text-4xl font-black leading-[1.02] tracking-tighter md:text-6xl">
            <MaskedLine delay={0.25}>
              <span className="text-metallic">Big companies have entire IT departments.</span>
            </MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="text-metallic">You have us.</span>
            </MaskedLine>
          </h1>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 pb-24 md:grid-cols-12 md:px-8" data-testid="about-story">
        <div className="md:col-span-6">
          <Reveal>
            <p className="text-base leading-relaxed text-[#C9D2DE]">
              Sempresera was founded in 2015 on a simple observation: the quality of a company's IT had become a
              function of its size. A thousand-person corporation gets 24/7 monitoring, a security operations center,
              tested backups and a CIO steering the roadmap. A ten-person firm gets a phone number for a repair shop.
            </p>
            <p className="mt-5 text-base leading-relaxed text-[#C9D2DE]">
              We thought that was backwards. Small businesses hold client data, run payroll, and bet their
              reputations on systems they have no time to manage — so we built the company that levels the playing
              field: one all-inclusive standard that gives smaller organizations the resources, security and support
              that very large corporations take for granted.
            </p>
            <p className="mt-5 text-base leading-relaxed text-[#C9D2DE]">
              More than a decade later, that focus hasn't drifted. As a national service provider, we still serve
              only small businesses across the United States — solo professionals, micro teams and established
              firms of up to 25 employees — from our home region of New England to every corner of the country.
            </p>
          </Reveal>
        </div>
        <div className="md:col-span-6">
          <Reveal delay={0.15}>
            <img
              src={IMAGES.office_team}
              alt="A small business team collaborating around a laptop in an office"
              className="clip-edge aspect-[4/3] w-full object-cover opacity-85"
            />
          </Reveal>
        </div>
      </section>

      <VendorMarquee />

      <section className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32" data-testid="about-promises">
        <Reveal>
          <p className="eyebrow">How we take care of you</p>
          <h2 className="mt-4 max-w-3xl font-display text-4xl font-bold tracking-tight md:text-5xl">
            <span className="text-metallic">Promises other MSPs</span>{" "}
            <span className="text-metallic">won't make.</span>
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2">
          {PROMISES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08}>
              <div className="glass h-full p-8 md:p-10" data-testid={`promise-${i}`}>
                <p className="font-display text-sm font-bold uppercase tracking-[0.25em] text-brand">0{i + 1}</p>
                <h3 className="mt-4 font-display text-2xl font-bold tracking-tight text-white">{v.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-[#C9D2DE]">{v.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-white/5 bg-panel/60" data-testid="about-values">
        <div className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32">
          <Reveal>
            <p className="eyebrow">What we believe</p>
            <h2 className="mt-4 font-display text-4xl font-bold tracking-tight md:text-5xl">
              <span className="text-metallic">Four values, kept since 2015.</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="glass h-full p-8">
                  <h3 className="font-display text-xl font-bold tracking-tight text-white">{v.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-[#C9D2DE]">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABanner
        title="Talk to a provider who answers."
        sub="Book a free consultation. We'll look at your setup, tell you plainly what we'd change, and show you what unlimited support actually feels like."
      />
    </>
  );
}
